import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import PrivacyPolicyDialog from './PrivacyPolicyDialog';
import ProfileSettingsDialog from './ProfileSettingsDialog';
import LoginForm from './LoginForm';
import Testimonials from './Testimonials';
import PricingSection from './PricingSection';
import FeatureSections from './FeatureSections';
import TechStackFooter from './TechStackFooter'; // Import the new component
import LandingSEO from './LandingSEO';
import AnimatedBackground from './AnimatedBackground';
import { Box, Container, CircularProgress, Typography, Link, Grid } from '@mui/material'; // Added Grid
import coolyLogo from '../../assets/coolyLogo.png';

const LandingLayout = ({
  userContext, 
  appLoading, 
  onLoginSuccess, 
  onPrivacyPolicyAccepted, 
  profileData, 
  onProfileSave, 
  onProfileDialogClose, 
  isSavingProfile, 
  profileSaveError,
}) => {
  const [showPrivacyDialog, setShowPrivacyDialog] = useState(false);
  const [showProfileDialog, setShowProfileDialog] = useState(false);

  useEffect(() => {
    if (appLoading) {
      // Still waiting for user data
      setShowPrivacyDialog(false);
      setShowProfileDialog(false);
      return;
    }

    if (userContext) {
      // User is logged in
      const consentNeeded = userContext.consentGiven !== 'true';
      const profileNeeded = userContext.consentGiven === 'true' && userContext.profileComplete !== true;
      
      setShowPrivacyDialog(consentNeeded);
      setShowProfileDialog(profileNeeded && !consentNeeded); // Show profile only if consent is given

    } else {
      // No user logged in
      setShowPrivacyDialog(false);
      setShowProfileDialog(false);
    }
  }, [userContext, appLoading]);

  const handleAcceptPrivacy = async () => { // Made async if onPrivacyPolicyAccepted is async
    if (onPrivacyPolicyAccepted) {
      await onPrivacyPolicyAccepted(); // Assuming this updates userContext via App.js
    }
  };

  const handleProfileComplete = async (data) => {
    if (onProfileSave) {
      await onProfileSave(data); // Assuming onProfileSave updates userContext
    }
  };
  
  const handleCloseProfileDialog = () => {
    if(onProfileDialogClose) {
        onProfileDialogClose(); // Parent can decide if it's okay to close
    }
  }

  if (appLoading && userContext === undefined) { // Show loading if userContext is not yet determined
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="100vh"
        sx={{ bgcolor: '#1E1E2D', color: 'white' }} // Dark background for loading
      >
        <CircularProgress color="inherit" />
      </Box>
    );
  }

  // Main page styling
  const mainPageBackgroundColor = '#1E1E2D';
  const mainTextColor = '#FFFFFF'; // Or a light grey like 'grey.300'

  return (
    <>
      <LandingSEO /> {/* Add the SEO component here instead of using Helmet directly */}
    
      <Box sx={{ bgcolor: mainPageBackgroundColor, color: mainTextColor, minHeight: '100vh', display: 'flex', flexDirection: 'column', position: 'relative' }}>
        <AnimatedBackground />
        <Container component="main" sx={{ flexGrow: 1, pb: 8, position: 'relative', zIndex: 10 }}>
          {/* Privacy Policy Dialog */}
          {userContext && showPrivacyDialog && (
            <PrivacyPolicyDialog
              open={showPrivacyDialog}
              onAccept={handleAcceptPrivacy}
            />
          )}

          {/* Profile Settings Dialog */}
          {userContext && showProfileDialog && (
             <ProfileSettingsDialog
                open={showProfileDialog}
                user={userContext} // Pass the full userContext or specific profileData
                profileData={profileData || userContext}
                onSave={handleProfileComplete}
                onClose={handleCloseProfileDialog} // Or a specific handler if different from onProfileDialogClose
                isSaving={isSavingProfile}
                saveError={profileSaveError}
            />
          )}

          {!userContext && !showPrivacyDialog && !showProfileDialog && (
            <>
              {/* Header with Logo */}
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 6 }}>
                <Box component="img" src={coolyLogo} alt="Coooly Logo" sx={{ width: 180, height: 'auto' }} />
              </Box>

              {/* Hero Section */}
              <Grid container spacing={4} justifyContent="center" alignItems="flex-start" sx={{ mb: 8, maxWidth: 1200, mx: 'auto' }}>
                
                {/* Row 1: Main Titles (Full Width) */}
                <Grid item xs={12} sx={{ textAlign: 'center', mb: { xs: 4, md: 4 } }}>
                  <Typography 
                    variant="h3" component="h1"
                    sx={{ color: '#A78BFA', fontWeight: 'bold', mb: 2, fontSize: { xs: '2rem', md: '2.5rem' } }}
                  >
                    Invoice Management for Dental Professionals
                  </Typography>
                  <Typography 
                    variant="h6" component="p"
                    sx={{ color: '#D8B4FE', fontSize: { xs: '1rem', md: '1.25rem' } }} 
                  >
                    Simplified Invoicing for Self-Employed Hygienists, Therapists, Nurses, and Locum Dentists
                  </Typography>
                </Grid>

                {/* Row 2: Content Box (Streamline + Login) on Desktop */}
                <Grid item xs={12} container spacing={3} justifyContent="center" alignItems="center">
                  {/* Streamline Box (Left on Desktop, Bottom on Mobile) */}
                  <Grid 
                    item 
                    xs={12} 
                    md={6}
                    order={{ xs: 2, md: 1 }} // Changed: Second on mobile, First on desktop
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ 
                      bgcolor: '#2A2A3E', 
                      p: { xs: 2, md: 3 }, // Reduced padding
                      borderRadius: 2, 
                      border: '1px solid rgba(138,43,226,0.2)',
                      width: '100%',
                      maxWidth: '500px',
                    }}>
                      <Typography variant="h5" sx={{ color: '#A78BFA', mb: 1.5, textAlign: 'center', fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                        Streamline Your Dental Practice Invoicing
                      </Typography>
                      <Typography sx={{ color: 'grey.300', textAlign: 'center', fontSize: '0.95rem' }}>
                        Designed specifically for self-employed dental professionals, our platform simplifies the invoicing process
                        for those working across multiple practices. With automated calculations
                        and secure data storage, you can focus on patient care while we handle the paperwork.
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Login Form (Right on Desktop, Top on Mobile) */}
                  <Grid 
                    item 
                    xs={12} 
                    md={6}
                    order={{ xs: 1, md: 2 }} // Changed: First on mobile, Second on desktop
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Box sx={{ maxWidth: 400, width: '100%' }}>
                      <LoginForm onLoginSuccess={onLoginSuccess} />
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              {/* Testimonials, Features, Pricing */}
              <Testimonials />
              <FeatureSections />
              <PricingSection />
            </>
          )}
          
          {/* If user is authenticated but dialogs are not showing, we could show a different view,
              or perhaps redirect to dashboard would already happen via OnboardingRedirect */}
          {userContext && !showPrivacyDialog && !showProfileDialog && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '50vh' 
            }}>
              <CircularProgress />
            </Box>
          )}
        </Container>

        {/* Tech Stack Footer - Added Before Regular Footer */}
        {!userContext && !showPrivacyDialog && !showProfileDialog && (
          <Box sx={{ position: 'relative', zIndex: 10 }}>
            <TechStackFooter />
          </Box>
        )}

        {/* Regular Footer */}
        <Box 
          component="footer" 
          sx={{ 
            py: 3, 
            px: 2, 
            bgcolor: '#2A2A3E',
            borderTop: '1px solid rgba(138, 43, 226, 0.1)',
            position: 'relative',
            zIndex: 10
          }}
        >
          <Container maxWidth="lg">
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 2 }}>
              <Link 
                component={RouterLink} 
                to="/blog" 
                sx={{ 
                  color: 'grey.400', 
                  textDecoration: 'none',
                  '&:hover': { color: '#A78BFA', textDecoration: 'underline' }
                }}
              >
                Blog
              </Link>
              <Link 
                component={RouterLink} 
                to="/privacy" 
                sx={{ 
                  color: 'grey.400', 
                  textDecoration: 'none',
                  '&:hover': { color: '#A78BFA', textDecoration: 'underline' }
                }}
              >
                Privacy Policy
              </Link>
              <Link 
                component={RouterLink} 
                to="/terms" 
                sx={{ 
                  color: 'grey.400', 
                  textDecoration: 'none',
                  '&:hover': { color: '#A78BFA', textDecoration: 'underline' }
                }}
              >
                Terms of Service
              </Link>
            </Box>
            <Typography 
              variant="body2" 
              align="center" 
              sx={{ color: 'grey.500' }}
            >
              &copy; {new Date().getFullYear()} Coooly Ltd. All rights reserved.
            </Typography>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default LandingLayout;
